import { useState, useEffect } from "react";
import NitroService from "../utils/nitro.service";
import {
  IGetQuotesParams,
  IOriginExtension,
  IQuotesByDestination,
} from "../types/IQuotes";
import { convertCompAnalysisQMD } from "../utils/convertCompAnalysisQMD";
import ILocation from "../types/ILocation";

export interface IQuotesResponse {
  quotes: IQuotesByDestination[];
  loading: boolean;
  error: Error | null;
}

export const useQuotesForDestinations = (
  quoteParams: IGetQuotesParams,
  refetchTrigger?: number,
  disabled?: boolean,
): IQuotesResponse => {
  const [quotesResponse, setQuotesResponse] = useState<IQuotesResponse>({
    quotes: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    // Skip fetch if disabled, or both destination and party are null (no search to perform)
    if (disabled || (!quoteParams.id && !quoteParams.party)) {
      setQuotesResponse({
        quotes: [],
        loading: false,
        error: null,
      });
      return;
    }

    setQuotesResponse((prevData) => ({
      ...prevData,
      loading: true,
      error: null,
    }));

    const fetchData = async () => {
      try {
        const quotesResult = await NitroService.getQuotesByDestination(
          quoteParams,
          0,
          0,
        );

        // Fetch and append pin types for origins
        // TODO: we also get details for favorite origins with rail turned on. do we need to do both sections or are we unecessarily duplicating calls?. now put this logic in convert function
        const originIds = new Set<string>();
        quotesResult.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin?.id) {
              originIds.add(quote.origin.id);
            }
          });
        });

        const locationDetailsMap = new Map<string, ILocation>();
        if (originIds.size > 0) {
          try {
            const detailsArray = await NitroService.getMultiLocationDetails2({
              ids: Array.from(originIds),
            });
            if (detailsArray) {
              detailsArray.forEach((details) => {
                if (details?.id) {
                  locationDetailsMap.set(details.id, details);
                }
              });
            }
          } catch (error) {
            console.error("Error fetching multi-location details:", error);
          }
        }

        quotesResult.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin.id && locationDetailsMap.has(quote.origin.id)) {
              const locationDetails = locationDetailsMap.get(quote.origin.id);
              if (locationDetails) {
                const origin = quote.origin as IOriginExtension;
                origin.pinType = locationDetails.pinType;
                origin.city = locationDetails.city;
                origin.details = locationDetails;
              }
            }
          });
        });

        const quotes = await convertCompAnalysisQMD(quotesResult);
        // TODO: should be user preference api

        setQuotesResponse({
          quotes,
          loading: false,
          error: null,
        });
      } catch (err) {
        setQuotesResponse((d) => ({
          ...d,
          loading: false,
          error: err as Error,
        }));
      }
    };

    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteParams.id,
    quoteParams.product,
    quoteParams.party,
    quoteParams.marketer,
    quoteParams.shipDate,
    quoteParams.currency,
    quoteParams.uom,
    refetchTrigger,
    disabled,
  ]);

  return quotesResponse;
};
